import { useEffect, useState } from 'react'
import type { User, UserManager } from 'oidc-client-ts'
import { setAuthTokenToCookies } from 'api/auth'
import { reportClientError } from 'services/errorReportingService'
import { isOidcSession } from './authMethod'

// App-wide, render-nothing coordinator. For a v2 (OIDC) session it mirrors the
// access token into the cookies the app reads for auth on every userLoaded
// (initial load + each silent renew). No-op for the v1 cookie-only path; imports
// oidc-client-ts lazily so it stays out of non-OIDC brands' bundles.
export default function useOidcSessionCoordinator(): void {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    // isOidcSession() reads storage, so only evaluate after mount.
    if (!isMounted || !isOidcSession()) {
      return
    }

    let userManager: UserManager | undefined
    let disposed = false

    const syncCookie = (user: User) => {
      if (user.access_token) {
        setAuthTokenToCookies(user.access_token)
      }
    }

    const onSilentRenewError = (error: Error) => {
      reportClientError(error, { context: 'oidcSilentRenew' }).catch(() => undefined)
    }

    import('./config')
      .then(({ createOidcUserManager }) => {
        if (disposed) {
          return
        }
        userManager = createOidcUserManager()
        userManager.events.addUserLoaded(syncCookie)
        userManager.events.addSilentRenewError(onSilentRenewError)

        return userManager.getUser().then((user) => {
          if (user) {
            syncCookie(user)
          }
        })
      })
      .catch((error) => reportClientError(error, { context: 'oidcSessionCoordinator' }))

    return () => {
      disposed = true
      if (userManager) {
        userManager.events.removeUserLoaded(syncCookie)
        userManager.events.removeSilentRenewError(onSilentRenewError)
      }
    }
  }, [isMounted])
}
